import { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { markDiscoveryBookedLocally } from 'src/features/organization/hooks/useOrgOnboardingGate';
import { ROUTE_PATHS } from 'src/shared/config/routes';

import { ButtonV2, Typography } from 'src/shared/components/ui';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (opts: {
        url: string;
        parentElement: HTMLElement;
        resize?: boolean;
      }) => void;
    };
  }
}

interface CalendlyOnboardingScreenProps {
  calendlyUrl: string;
  userName: string;
  onSkip?: () => void;
  description?: string;
  headerAction?: ReactNode;
}

const CalendlyOnboardingScreen = ({
  calendlyUrl,
  userName,
  onSkip,
  description,
  headerAction
}: CalendlyOnboardingScreenProps) => {
  const navigate = useNavigate();
  const embedRef = useRef<HTMLDivElement>(null);
  const [hasBooked, setHasBooked] = useState(false);
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );

  useEffect(() => {
    const script = document.createElement('script');

    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      if (embedRef.current && window.Calendly) {
        const url = new URL(calendlyUrl);

        url.searchParams.set('utm_source', 'cavela_onboarding');
        window.Calendly.initInlineWidget({
          url: url.toString(),
          parentElement: embedRef.current,
          resize: false
        });
      }
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [calendlyUrl]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (
        e.origin === 'https://calendly.com' &&
        (e.data as { event?: string })?.event === 'calendly.event_scheduled'
      ) {
        if (selectedOrganization?.id) {
          markDiscoveryBookedLocally(selectedOrganization.id);
        }
        if (onSkip) {
          onSkip();
        } else {
          setHasBooked(true);
        }
      }
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
  }, [selectedOrganization?.id, onSkip]);

  return (
    <div className="max-w-[1200px] mx-auto flex flex-col gap-6 px-6 pt-6 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
        <div className="flex max-w-xl flex-col gap-0">
          <Typography as="h1" variant="h2" weight="bold">
            {hasBooked
              ? `Your call is booked, ${userName}!`
              : `Welcome to Cavela, ${userName}!`}
          </Typography>
          <Typography className="text-secondary-text">
            {hasBooked
              ? 'You can now start building your product.'
              : (description ??
                'Book a discovery call with our team to get started with your sourcing journey.')}
          </Typography>
        </div>
        {hasBooked ? (
          <ButtonV2 onPress={() => navigate(ROUTE_PATHS.BUILD_ANYTHING)}>
            Continue
          </ButtonV2>
        ) : (
          headerAction
        )}
      </div>
      <div className="w-full rounded-xl border border-gray-4 bg-gray-2 p-4">
        <div className="min-h-[660px]">
          <div
            ref={embedRef}
            className="h-[660px] w-full overflow-hidden rounded-xl"
            style={{ minWidth: 320 }}
          />
        </div>
      </div>
      {!hasBooked && onSkip && (
        <div className="flex justify-center">
          <ButtonV2 color="tertiary" onPress={onSkip}>
            Skip for now
          </ButtonV2>
        </div>
      )}
    </div>
  );
};

export default CalendlyOnboardingScreen;
